"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useFavorites } from "@/hooks/useFavorites";
import type { FavoritesPage } from "@/sanity/lib/types";

const BAG_STORAGE_KEY = "vault88-cart";

export function FavoritesClient({ content, heroImageUrl }: { content: FavoritesPage; heroImageUrl: string | null }) {
  const { items, removeItem, patchPictureUrls } = useFavorites();
  const [bagAdded, setBagAdded] = useState<Record<string, boolean>>({});
  const [brokenImageIds, setBrokenImageIds] = useState<Set<string>>(new Set());

  const itemIdsKey = items.map((item) => item.id).sort().join(",");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const ids = itemIdsKey ? itemIdsKey.split(",") : [];
    if (ids.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch("/api/items/refresh-images", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids })
        });
        if (!response.ok) return;
        const data = (await response.json()) as { items?: Array<{ id: string; pictureUrl: string }> };
        if (cancelled || !data.items?.length) return;

        const map: Record<string, string> = {};
        for (const entry of data.items) {
          if (entry.pictureUrl) map[entry.id] = entry.pictureUrl;
        }
        await patchPictureUrls(map);
        setBrokenImageIds(new Set());
      } catch (error) {
        console.error("[Favorites] Failed to refresh image URLs", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [itemIdsKey, patchPictureUrls]);

  const addToBag = (item: (typeof items)[number]) => {
    if (item.soldOut) {
      return;
    }

    try {
      const stored = window.localStorage.getItem(BAG_STORAGE_KEY);
      const existing = stored ? (JSON.parse(stored) as Array<{ id: string }>) : [];
      if (existing.some((e) => e.id === item.id)) {
        setBagAdded((prev) => ({ ...prev, [item.id]: true }));
        return;
      }
      const next = [
        ...existing,
        { id: item.id, itemName: item.itemName, pictureUrl: item.pictureUrl, tagPrice: item.tagPrice, metal: item.metal, stone: item.stone }
      ];
      window.localStorage.setItem(BAG_STORAGE_KEY, JSON.stringify(next));
      window.dispatchEvent(new StorageEvent("storage", { key: BAG_STORAGE_KEY, newValue: JSON.stringify(next) }));
      setBagAdded((prev) => ({ ...prev, [item.id]: true }));
    } catch {
      // ignore
    }
  };
  const hasItems = items.length > 0;

  return (
    <div className="-mt-[48px] sm:-mt-[84px]">
      {heroImageUrl ? (
        <section className="relative h-[55vh] min-h-[360px] w-full overflow-hidden">
          <Image
            src={heroImageUrl}
            alt={content.heroImage?.alt ?? content.heading ?? "Your Favorites"}
            fill
            priority
            className="object-cover"
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-black/30" />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <h1 className="text-4xl font-semibold text-white font-[var(--font-display)] sm:text-5xl drop-shadow-lg">
              {content.heading}
            </h1>
          </div>
        </section>
      ) : null}

      <div className="px-4 py-24">
        <div className="mx-auto max-w-4xl space-y-8 text-center">
          {!heroImageUrl ? (
            <h1 className="text-4xl font-semibold text-slate-900 font-[var(--font-display)]">{content.heading}</h1>
          ) : null}

          {hasItems ? (
            <>
              <p className="text-slate-600">{content.subtitle}</p>

              <div className="border border-slate-200 bg-white text-left">
                {items.map((item, index) => (
                  <article
                    key={item.id}
                    className={`relative flex flex-col gap-4 p-4 sm:flex-row${index < items.length - 1 ? " border-b border-slate-200" : ""}`}
                  >
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="absolute right-3 top-3 hidden sm:flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-sm font-medium text-slate-500 transition hover:bg-slate-200 hover:text-slate-900"
                      aria-label="Remove from favorites"
                    >
                      ×
                    </button>

                    <Link href={`/pieces/${item.id}`} className="relative block h-48 flex-shrink-0 overflow-hidden bg-white sm:h-40 sm:w-40">
                      {item.pictureUrl && !brokenImageIds.has(item.id) ? (
                        <Image
                          src={item.pictureUrl}
                          alt={item.itemName || "Jewelry item"}
                          fill
                          className="object-contain"
                          sizes="160px"
                          onError={() =>
                            setBrokenImageIds((prev) => {
                              if (prev.has(item.id)) return prev;
                              const next = new Set(prev);
                              next.add(item.id);
                              return next;
                            })
                          }
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-slate-400 text-xs">No image</div>
                      )}
                    </Link>

                    <div className="flex flex-1 flex-col justify-between gap-3 sm:pr-8">
                      <div>
                        <Link href={`/pieces/${item.id}`} className="text-lg font-semibold text-slate-900 hover:underline hover:underline-offset-2">
                          {item.itemName || "Untitled"}
                        </Link>
                        <p className="text-sm text-slate-500 mt-0.5">
                          {[item.metal, item.stone].filter(Boolean).join(" · ") || "—"}
                        </p>
                      </div>
                      <p className="text-slate-900 font-medium">
                        {item.tagPrice ? `$${item.tagPrice.toLocaleString()}` : "Price on request"}
                      </p>
                      <div className="flex flex-col gap-2 sm:hidden">
                        <button
                          type="button"
                          onClick={() => addToBag(item)}
                          disabled={Boolean(item.soldOut)}
                          className="w-full inline-flex items-center justify-center rounded-sm bg-[#c4a668] px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
                        >
                          {item.soldOut ? content.soldOutText : bagAdded[item.id] ? content.inBagText : content.addToBagText}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="w-full inline-flex items-center justify-center rounded-sm border border-slate-200 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.3em] text-slate-600 transition hover:border-slate-400 hover:text-slate-900"
                        >
                          {content.removeText}
                        </button>
                      </div>
                      <div className="hidden sm:flex flex-wrap items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => addToBag(item)}
                          disabled={Boolean(item.soldOut)}
                          className="inline-flex items-center justify-center rounded-sm bg-[#c4a668] px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
                        >
                          {item.soldOut ? content.soldOutText : bagAdded[item.id] ? content.inBagText : content.addToBagText}
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              <Link
                href="/cart"
                className="inline-flex items-center justify-center rounded-sm bg-royal px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-white transition hover:-translate-y-0.5"
              >
                {content.contactButtonText}
              </Link>
            </>
          ) : (
            <div className="space-y-6">
              <p className="text-slate-600">{content.emptyMessage}</p>
              <Link
                href={(content.browseButtonLink ?? "/browse") as "/browse"}
                className="inline-flex items-center justify-center rounded-sm bg-[#c4a668] px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-white transition hover:-translate-y-0.5"
              >
                {content.browseButtonText}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
